import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Creates a `useState` like tuple for handling data which should be persisted through `Storage`.
 * The `state` is initialized to the value which exists in `Storage` or some fallback. The `setState`
 * function updates both the state (causing rerenders) and the `Storage` itself.
 *
 * The `ensure` function, if provided, is used to validate the data retrieved from `Storage`. If the data does not pass validation, the fallback value will be used instead.
 *
 * **Note:** `Storage` can be edited by anyone, and data taken from `Storage` should never be trusted (i.e. inserted directly into the DOM or a database) without validation. It should also be noted that `useStorage`'s state will not be perfectly up-to-date with the actual `Storage`; if that is critical, you will have to continually call `Storage.getItem`, and `useStorage` isn't for your use-case.
 *
 * @param storage the `Storage` object to use (either `localStorage` or `sessionStorage`)
 * @param key the key to use for storing in `Storage`
 * @param fallback the value to fall back to in case the value in `Storage` is missing or corrupted
 * @param ensure an optional function to validate the data retrieved from `Storage`. If the data does not pass the validation, the fallback value is used instead.
 *
 * @returns `[state, setState, { clearStorage, saveNow }]` where:
 * - `state` is the current value
 * - `setState` updates both the state and Storage
 * - `clearStorage` removes the item from Storage
 * - `saveNow` immediately saves the current state to Storage
 *
 * @example
 * const MyComponent = () => {
 *     // the first parameter is the key to store the data in
 *     // the second parameter is the value to default to, in case the data
 *     // doesn't yet exist or is corrupted somehow
 *     // the optional ensure function validates the value fetched from storage
 *     const [canSendHeart, setCanSendHeart, { clearStorage }] = useStorage("has-seen-popup", true, (item) => typeof item === "boolean");
 *
 *     return (
 *        <>
 *         {canSendHeart && (
 *            <button
 *              onClick={() => {
 *                // this will update both the state (causing a re-render) and `Storage`
 *                setCanSendHeart(false);
 *              }}
 *            >
 *              Send Heart
 *            </button>
 *          )}
 *        </>
 *    );
 * }
 */
export function useStorage<S>(
  storage: Storage,
  key: string,
  fallback: S | (() => S),
  ensure?: (item: unknown) => boolean,
) {
  const [state, setState] = useState<S>(() =>
    getStoredValue(storage, key, fallback, ensure),
  );

  useSaveToStorage(state, storage, key);

  const clearStorage = useCallback(() => {
    storage.removeItem(key);
  }, [storage, key]);

  const saveNow = useCallback(() => {
    storage.setItem(key, JSON.stringify(state));
  }, [state, key, storage]);

  return [
    state,
    setState,
    {
      clearStorage,
      saveNow,
    },
  ] as const;
}

/**
 * Creates a `useState` like tuple for handling data which should be persisted through `localStorage`.
 * The `state` is initialized to the value which exists in `localStorage` or some fallback. The `setState`
 * function updates both the state (causing rerenders) and `localStorage` itself.
 *
 * The `ensure` function, if provided, is used to validate the data retrieved from `localStorage`. If the data does not pass validation, the fallback value will be used instead.
 *
 * **Note:** `localStorage` can be edited by anyone, and data taken from `localStorage` should never be trusted (i.e. inserted directly into the DOM or a database) without validation. It should also be noted that `useLocalStorage`'s state will not be perfectly up-to-date with the actual `localStorage`; if that is critical, you will have to continually call `localStorage.getItem`, and `useLocalStorage` isn't for your use-case.
 *
 * @param key the key to use for storing in `localStorage`
 * @param fallback the value to fall back to in case the value in `localStorage` is missing or corrupted
 * @param ensure an optional function to validate the data retrieved from `localStorage`. If the data does not pass the validation, the fallback value is used instead.
 *
 * @returns `[state, setState, { clearStorage, saveNow }]` where:
 * - `state` is the current value
 * - `setState` updates both the state and localStorage
 * - `clearStorage` removes the item from localStorage
 * - `saveNow` immediately saves the current state to localStorage
 *
 * @example
 * const MyComponent = () => {
 *     // the first parameter is the key to store the data in
 *     // the second parameter is the value to default to, in case the data
 *     // doesn't yet exist or is corrupted somehow
 *     // the optional ensure function validates the value fetched from localStorage
 *     const [canSendHeart, setCanSendHeart] = useLocalStorage("has-seen-popup", true, (item) => typeof item === "boolean");
 *
 *     return (
 *        <>
 *         {canSendHeart && (
 *            <button
 *              onClick={() => {
 *                // this will update both the state (causing a re-render) and `localStorage`
 *                setCanSendHeart(false);
 *              }}
 *            >
 *              Send Heart
 *            </button>
 *          )}
 *        </>
 *    );
 * }
 */

export function useLocalStorage<S>(
  ...params: RemoveFirst<Parameters<typeof useStorage<S>>>
) {
  return useStorage<S>(localStorage, ...params);
}

/**
 * Creates a `useState` like tuple for handling data which should be persisted through `sessionStorage`.
 * The `state` is initialized to the value which exists in `sessionStorage` or some fallback. The `setState`
 * function updates both the state (causing rerenders) and `sessionStorage` itself.
 *
 * The `ensure` function, if provided, is used to validate the data retrieved from `sessionStorage`. If the data does not pass validation, the fallback value will be used instead.
 *
 * **Note:** `sessionStorage` can be edited by anyone, and data taken from `sessionStorage` should never be trusted (i.e. inserted directly into the DOM or a database) without validation. It should also be noted that `useSessionStorage`'s state will not be perfectly up-to-date with the actual `sessionStorage`; if that is critical, you will have to continually call `sessionStorage.getItem`, and `useSessionStorage` isn't for your use-case.
 *
 * @param key the key to use for storing in `sessionStorage`
 * @param fallback the value to fall back to in case the value in `sessionStorage` is missing or corrupted
 * @param ensure an optional function to validate the data retrieved from `sessionStorage`. If the data does not pass the validation, the fallback value is used instead.
 *
 * @returns `[state, setState, { clearStorage, saveNow }]` where:
 * - `state` is the current value
 * - `setState` updates both the state and sessionStorage
 * - `clearStorage` removes the item from sessionStorage
 * - `saveNow` immediately saves the current state to sessionStorage
 *
 * @example
 * const MyComponent = () => {
 *     // the first parameter is the key to store the data in
 *     // the second parameter is the value to default to, in case the data
 *     // doesn't yet exist or is corrupted somehow
 *     // the optional ensure function validates the value fetched from sessionStorage
 *     const [canSendHeart, setCanSendHeart] = useSessionStorage("has-seen-popup", true, (item) => typeof item === "boolean");
 *
 *     return (
 *        <>
 *         {canSendHeart && (
 *            <button
 *              onClick={() => {
 *                // this will update both the state (causing a re-render) and `sessionStorage`
 *                setCanSendHeart(false);
 *              }}
 *            >
 *              Send Heart
 *            </button>
 *          )}
 *        </>
 *    );
 * }
 */
export function useSessionStorage<S>(
  ...params: RemoveFirst<Parameters<typeof useStorage<S>>>
) {
  return useStorage<S>(sessionStorage, ...params);
}

/**
 * A hook to persist the given state to `Storage` (either `localStorage` or `sessionStorage`) and automatically save it during certain events like when the component unmounts or the page visibility changes.
 *
 * The state is stored in the `Storage` and can be retrieved later, even after a page reload, allowing persistence across sessions.
 *
 * **Note:** This hook debounces writes on state changes to limit `Storage` churn. On tab hide, `beforeunload`, and `pagehide` it persists **synchronously** so a refresh or close cannot skip the write (a debounced handler alone would run too late). The latest value is taken from an internal ref updated every render.
 *
 * @param state the current state that should be persisted to `Storage`
 * @param storage the `Storage` object to use (either `localStorage` or `sessionStorage`)
 * @param key the key to use for storing the data in `Storage`
 * @param enabled if `false` then will not save the state to the storage. defaults to `true`
 *
 * @returns `void` (this hook does not return anything, it only performs side effects)
 *
 * @example
 * const MyComponent = () => {
 *     const [data, setData] = useState("initialValue");
 *
 *     // The state is automatically saved to `localStorage` with the key "my-key"
 *     // The value will be saved when the component unmounts or page visibility changes.
 *     useSaveToStorage(data, localStorage, "my-key");
 *
 *     return (
 *        <div>
 *           <button onClick={() => setData("newValue")}>Update State</button>
 *        </div>
 *     );
 * }
 */
export function useSaveToStorage<T>(
  state: T,
  storage: Storage,
  key: string,
  enabled = true,
) {
  const stateRef = useRef<T>(state);
  stateRef.current = state;

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const saveToStorage = useCallback(
    debounce(() => {
      if (!enabledRef.current) return;
      try {
        storage.setItem(key, JSON.stringify(stateRef.current));
      } catch (error) {
        console.error("Failed to save state to Storage", error);
      }
    }, 1000),
    [key, storage],
  );

  /** Must be synchronous: debounced `saveToStorage` never runs before the tab is gone on refresh/close. */
  const persistSynchronously = useCallback(() => {
    if (!enabledRef.current) return;
    try {
      storage.setItem(key, JSON.stringify(stateRef.current));
    } catch (error) {
      console.error("Failed to save state to Storage", error);
    }
  }, [key, storage]);

  // Persist on state changes (debounced). Without this, changes are only persisted
  // on visibility/unload/unmount, which can miss the latest user edits on refresh.
  useEffect(() => {
    saveToStorage();
  }, [state, saveToStorage]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        persistSynchronously();
      } else {
        saveToStorage();
      }
    };

    window.addEventListener("beforeunload", persistSynchronously);
    window.addEventListener("pagehide", persistSynchronously);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      saveToStorage();
      saveToStorage.flush();
      persistSynchronously();
      window.removeEventListener("beforeunload", persistSynchronously);
      window.removeEventListener("pagehide", persistSynchronously);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [saveToStorage, persistSynchronously]);
}

/**
 *
 * @param storage the `Storage` object to use (either `localStorage` or `sessionStorage`)
 * @param key the key to use for finding the item in `Storage`
 * @param fallback the value to fall back to in case the value in `Storage` is missing or corrupted
 * @param ensure an optional function to validate the data retrieved from `Storage`. If the data does not pass the validation, the fallback value is used instead.
 *
 */
export function getStoredValue<S>(
  storage: Storage,
  key: string,
  fallback: (() => S) | S,
  ensure?: (val: unknown) => boolean,
) {
  const value = storage.getItem(key);
  if (value !== null) {
    try {
      const parsed = JSON.parse(value);
      if (ensure?.(parsed) ?? true) return parsed as S;
    } catch {
      // Parsing failed, use fallback
    }
  }
  return typeof fallback === "function" ? (fallback as () => S)() : fallback;
}

export type RemoveFirst<T extends any[]> = T extends [
  infer _Head,
  ...infer Rest,
]
  ? Rest
  : never;
